import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  Link,
  Switch,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { SafeSendIcon, SafeSendOnIcon, biInfoIcon } from "@icons";
import {
  type FA12Transfer,
  type FA2Transfer,
  type ImplicitAccount,
  type TezTransfer,
  makeToolkit,
  totalFee,
} from "@umami/chains";
import { estimateSafeSendAction, isSafeSendSupported, send } from "@umami/safesend";
import { useAsyncActionHandler, useSelectedNetwork } from "@umami/state";
import { CustomError } from "@umami/utils";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";

import { useColor } from "@/styles/useColor";

type SafeSendTileProps = {
  operation: TezTransfer | FA12Transfer | FA2Transfer;
  signer: ImplicitAccount;
  balance: string;
};

export const SafeSendTile = ({ operation, signer, balance }: SafeSendTileProps) => {
  const color = useColor();
  const { register, setValue, watch } = useFormContext();
  const network = useSelectedNetwork();
  const isSafeSendEnabled = watch("isSafeSendEnabled");
  const safeSendComments = watch("safeSendComments");
  const safeSendFee = watch("safeSendFee");
  const { isLoading, handleAsyncActionUnsafe } = useAsyncActionHandler();

  const estimateSafeSend = () =>
    handleAsyncActionUnsafe(async () => {
      const tezToolkit = await makeToolkit({
        type: "fake",
        signer,
        network,
      });

      const { batch } = await send(
        tezToolkit,
        network,
        operation,
        safeSendComments || undefined,
        balance
      );

      const estimates = await estimateSafeSendAction(tezToolkit, batch);
      const safeSendFee = totalFee(estimates);
      setValue("safeSendFee", safeSendFee);
    }).catch(e => {
      setValue("safeSendFee", 0);
      throw new CustomError("Failed to estimate safe send fees " + e.message);
    });

  useEffect(() => {
    setValue("safeSendFeeLoading", isLoading);
  }, [isLoading, setValue]);

  if (!isSafeSendSupported(network)) {
    return null;
  }

  const IconComponent = isSafeSendEnabled ? SafeSendOnIcon : SafeSendIcon;

  return (
    <Flex flexDirection="column" width="100%">
      <Flex
        alignItems="center"
        justifyContent="flex-start"
        gap="16px"
        width="100%"
        padding="16px"
        background={color("100")}
        borderRadius="4px"
      >
        <IconComponent width="40px" height="24px" color={color("blue", "black")} />
        <Flex flexDirection="column" gap="6px">
          <Text
            alignSelf="stretch"
            color={color("700")}
            fontSize="16px"
            fontWeight="bold"
            lineHeight="18px"
          >
            Safe Send
          </Text>
          <Text alignSelf="stretch" color={color("700")} fontSize="12px" lineHeight="18px">
            Safe send allows you to undo a transaction sent to the wrong address.{" "}
            <Link color={color("500")} fontWeight="bold" href="https://safesend.to/" isExternal>
              Learn more
            </Link>
          </Text>
        </Flex>
        <Switch
          sx={{
            "& .chakra-switch__track[data-checked]": {
              backgroundColor: "#2C2CE5 !important",
            },
            "& .chakra-switch__thumb[data-checked]": {
              backgroundColor: "white",
            },
          }}
          colorScheme="blue"
          isChecked={isSafeSendEnabled}
          size="md"
          {...register("isSafeSendEnabled")}
          onChange={async e => {
            setValue("isSafeSendEnabled", e.target.checked);

            if (e.target.checked && safeSendFee === 0) {
              await estimateSafeSend();
            }
          }}
        />
      </Flex>

      {isSafeSendEnabled && (
        <FormControl marginTop="10px" marginBottom="10px">
          <FormLabel
            alignItems="center"
            gap="6px"
            display="flex"
            margin="0 0 12px 0"
            color={color("900")}
            fontSize="18px"
            fontWeight="600"
            lineHeight="24px"
          >
            Reference
            <Tooltip
              padding="8px"
              color={color("white")}
              fontSize="14px"
              background={color("black")}
              borderRadius="4px"
              arrowSize={8}
              hasArrow
              label="Add a short comment to help identify this transaction"
              offset={[0, 8]}
              placement="bottom-start"
            >
              <Box alignItems="center" display="inline-flex" cursor="help">
                <Icon as={biInfoIcon} boxSize="24px" color={color("400")} />
              </Box>
            </Tooltip>
          </FormLabel>
          <Input
            maxLength={140}
            placeholder="Add a comment (optional)"
            {...register("safeSendComments")}
            height="48px"
            padding="0 16px"
            background="white"
            borderWidth="1.5px"
            borderColor={color("300")}
            borderRadius="6px"
            _focusWithin={{
              borderColor: color("400"),
              color: color("900"),
            }}
            _placeholder={{
              color: color("400"),
              fontSize: "18px",
              fontWeight: "400",
            }}
          />
        </FormControl>
      )}
    </Flex>
  );
};
